// The first analysis, as the welcome and the dashboard tell it. The server queues it on its own once
// the last account's games are in (DiagnosisRequestService.queueFirstAnalysis) and emails the report
// when it ends; these pages only say where it is. One function so both say the same thing.

import { isRunning, jobPercent, jobTimeLeft } from "./diagnosisView.ts";
import type { JobProgress } from "./diagnosisView.ts";
import { t } from "../i18n/welcome.ts";
import { language } from "../services/i18n.ts";

/** "about 4 minutes" as a sentence can open: "About 4 minutes left." */
const timeLeftSentence = (job: JobProgress, now: Date): string | null => {
    const left = jobTimeLeft(job, now);
    return left === null ? null : t("timeLeft", left);
};

/** What the first analysis reads: the free games, the same number DiagnosisAllowance.FREE_GAMES gives. */
export const FIRST_ANALYSIS_GAMES = 300;

export interface AnalysisJob extends JobProgress {
    id: number;
    kind: string;
    finishedAt?: string | null;
}

export interface AnalysisStatus {
    unanalysed: number;
    job: AnalysisJob | null;
    /** subscribedUntil is there only while the subscription runs (DiagnosisRequestService.Allowance). */
    allowance?: { subscribed: boolean; subscribedUntil?: string | null };
}

export type FirstAnalysisState = "coming" | "running" | "done" | "failed";

export interface FirstAnalysisView {
    state: FirstAnalysisState;
    title: string;
    text: string;
    /** 0 to 100 while it runs, null otherwise. */
    percent: number | null;
}

/** Null when there is nothing to say: no games to read, and none read. */
export function firstAnalysisView(status: AnalysisStatus | null, now: Date = new Date()): FirstAnalysisView | null {
    if (!status) return null;
    const job = status.job;
    if (!job) {
        if (status.unanalysed === 0) return null;
        return {
            state: "coming",
            title: t("comingTitle", FIRST_ANALYSIS_GAMES),
            text: t("comingText", FIRST_ANALYSIS_GAMES),
            percent: null,
        };
    }
    const games = job.gamesTotal ?? FIRST_ANALYSIS_GAMES;
    if (isRunning(job)) {
        const left = timeLeftSentence(job, now);
        return {
            state: "running",
            title: t("runningTitle", games),
            text: job.status === "queued" ? t("queuedText") : t("runningText", left),
            percent: jobPercent(job),
        };
    }
    if (job.status === "done") {
        return {
            state: "done",
            title: t("readyTitle"),
            text: t("doneText", games),
            percent: null,
        };
    }
    return {
        state: "failed",
        title: t("failedTitle"),
        text: t("failedText"),
        percent: null,
    };
}

/**
 * The month free that came with the account (DiagnosisAllowance.TRIAL), said under the first analysis: that one
 * is the last 100, and from here on every new game is read within minutes. Null without a subscription running.
 */
export function trialNote(status: AnalysisStatus | null, now: Date = new Date()): string | null {
    const until = status?.allowance?.subscribedUntil;
    if (!status?.allowance?.subscribed || !until) return null;
    const end = new Date(until);
    if (!(end.getTime() > now.getTime())) return null;
    const day = end.toLocaleDateString(language() === "es" ? "es-ES" : "en-GB", { day: "numeric", month: "long" });
    return t("trialNote", day);
}

/** How long a finished report is announced on the dashboard, unless it has been opened. */
export const READY_SHOWN_DAYS = 7;

export interface AnalysisInvitation {
    kind: "running" | "ready";
    title: string;
    lead: string;
    action: string;
    percent: number | null;
}

/**
 * The strip on the dashboard: an analysis running, or a report finished in the last week that this
 * device has not opened yet. Anything else is the report page's to say.
 */
export function analysisInvitation(status: AnalysisStatus | null, seenJob: number | null,
                                   now: Date = new Date()): AnalysisInvitation | null {
    const job = status?.job;
    if (!job) return null;
    const games = job.gamesTotal ?? FIRST_ANALYSIS_GAMES;
    if (isRunning(job) && job.kind === "report") {
        const left = timeLeftSentence(job, now);
        return {
            kind: "running",
            title: t("runningTitle", games),
            lead: t("stripRunning", left),
            action: t("seeHowItGoes"),
            percent: jobPercent(job),
        };
    }
    // Upkeep (the night's games of a subscription, old rows read with new rules) is no news
    if (job.kind !== "report" || job.status !== "done" || job.id === seenJob || !job.finishedAt) return null;
    const age = now.getTime() - new Date(job.finishedAt).getTime();
    if (!(age >= 0 && age < READY_SHOWN_DAYS * 86_400_000)) return null;
    return {
        kind: "ready",
        title: t("readyTitle"),
        lead: t("stripReady", games),
        action: t("readReport"),
        percent: null,
    };
}
